import { Link } from 'react-router-dom'
import styled from 'styled-components'

export const MarketingPage = styled.main`
    background-color: var(--surface-0);
    color: var(--ink-inverse);
`

export const MarketingHero = styled.section`
    position: relative;
    overflow: hidden;
    background-color: var(--brand);
    padding: 56px 0 48px;
    display: flex;
    align-items: flex-end;

    @media (max-width: 999px) {
        padding: 44px 0 36px;
    }

    @media (max-width: 690px) {
        padding: 36px 0 28px;
    }
`

export const MarketingInner = styled.div`
    position: relative;
    z-index: 1;
    width: min(1120px, calc(100% - 80px));
    margin: 0 auto;

    @media (max-width: 999px) {
        width: calc(100% - 48px);
    }

    @media (max-width: 690px) {
        width: calc(100% - 40px);
    }
`

export const MarketingIntro = styled.div`
    max-width: 820px;

    h1 {
        margin: 0 0 20px;
        color: var(--ink);
        font-family: var(--font-display);
        font-size: 72px;
        font-weight: 400;
        line-height: 0.95;
        letter-spacing: 1px;
        text-transform: uppercase;
    }

    p {
        margin: 0;
        max-width: 38em;
        color: var(--ink);
        font-family: var(--font-body);
        font-size: var(--text-body);
        line-height: var(--leading-body);
    }

    @media (max-width: 1300px) {
        h1 {
            font-size: 58px;
        }
    }

    @media (max-width: 999px) {
        h1 {
            font-size: 47px;
        }
    }

    @media (max-width: 690px) {
        h1 {
            font-size: 40px;
        }

        p {
            font-size: 16px;
            line-height: 24px;
        }
    }
`

export const MarketingSection = styled.section<{ $alt?: boolean }>`
    background-color: ${(props) => (props.$alt ? 'var(--surface-1)' : 'var(--surface-0)')};
    padding: 88px 0;

    @media (max-width: 999px) {
        padding: 64px 0;
    }

    @media (max-width: 690px) {
        padding: 48px 0;
    }
`

export const SplitBlock = styled.div`
    display: grid;
    grid-template-columns: minmax(240px, 0.82fr) minmax(0, 1.18fr);
    align-items: start;
    column-gap: 64px;
    row-gap: 24px;

    h2 {
        margin: 0;
        font-family: var(--font-display);
        font-size: 56px;
        font-weight: 400;
        line-height: 0.92;
        letter-spacing: 1px;
        text-transform: uppercase;
        color: var(--brand);
    }

    p {
        margin: 0;
        max-width: 38em;
        font-family: var(--font-body);
        font-size: var(--text-body);
        line-height: var(--leading-body);
        color: rgba(255, 255, 255, 0.86);
    }

    a {
        color: var(--brand);
        font-weight: 600;
        text-decoration: underline;
        text-underline-offset: 2px;

        &:hover {
            color: var(--brand-hover);
        }
    }

    p + p {
        margin-top: 16px;
    }

    @media (max-width: 999px) {
        grid-template-columns: 1fr;

        h2 {
            font-size: 44px;
        }
    }

    @media (max-width: 690px) {
        h2 {
            font-size: 36px;
        }

        p {
            font-size: 16px;
            line-height: 24px;
        }
    }
`

export const CardGrid = styled.div<{ $columns?: number }>`
    display: grid;
    grid-template-columns: repeat(${(props) => props.$columns || 2}, minmax(0, 1fr));
    gap: 18px;
    margin-top: 40px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
        margin-top: 28px;
    }
`

export const FeatureCard = styled.article`
    background-color: var(--surface-2);
    border: 1px solid var(--color-border-dark);
    padding: 32px 28px 36px;

    h3 {
        margin: 0 0 12px;
        font-family: var(--font-heading);
        font-size: 22px;
        font-weight: 600;
        line-height: 1.25;
        letter-spacing: -0.01em;
        text-transform: none;
        color: var(--brand);
    }

    p {
        margin: 0;
        color: rgba(255, 255, 255, 0.78);
        font-size: 16px;
        line-height: 1.55;
    }

    @media (max-width: 690px) {
        padding: 24px 20px 28px;

        h3 {
            font-size: 18px;
        }
    }
`

export const StepGrid = styled.ol`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 18px;
    margin: 40px 0 0;
    padding: 0;
    list-style: none;

    @media (max-width: 999px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 690px) {
        grid-template-columns: 1fr;
        margin-top: 28px;
    }
`

export const StepCard = styled.li`
    background-color: var(--surface-2);
    border: 1px solid var(--color-border-dark);
    padding: 28px 24px 32px;

    span {
        display: block;
        margin-bottom: 16px;
        color: var(--brand);
        font-family: var(--font-display);
        font-size: 36px;
        line-height: 1;
        letter-spacing: 1px;
    }

    p {
        margin: 0;
        color: #ffffff;
        font-family: var(--font-heading);
        font-size: 18px;
        font-weight: 600;
        line-height: 1.35;
    }
`

export const CtaBand = styled.section`
    width: 100%;
    margin: 0;
    background-color: var(--brand);
    text-align: center;
    color: var(--ink);
    padding: 72px var(--page-gutter, 40px);

    h2 {
        margin: 0 0 16px;
        font-family: var(--font-display);
        font-size: 48px;
        line-height: 1;
        letter-spacing: 1px;
        text-transform: uppercase;
        color: var(--ink);
    }

    p {
        margin: 0 auto 28px;
        max-width: 36em;
        font-size: 18px;
        line-height: 1.55;
        color: var(--ink);
    }

    @media (max-width: 999px) {
        padding: 56px 28px;
    }

    @media (max-width: 690px) {
        padding: 40px 20px;

        h2 {
            font-size: 36px;
        }

        p {
            font-size: 16px;
            line-height: 24px;
        }
    }
`

export const CtaLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 160px;
    background: var(--surface-0);
    color: var(--ink-inverse);
    border-radius: var(--radius-pill);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.15);
    font-family: var(--font-heading);
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.02em;
    line-height: 1;
    padding: 16px 32px;
    text-decoration: none;
    transition: background-color var(--duration-ui) var(--ease-out),
        color var(--duration-ui) var(--ease-out);

    &:hover {
        background: var(--surface-2);
        color: var(--ink-inverse);
    }
`

export const FaqList = styled.div`
    margin-top: 48px;
    border-top: 1px solid var(--color-border-dark);

    @media (max-width: 690px) {
        margin-top: 36px;
    }
`

export const FaqItem = styled.details`
    border-bottom: 1px solid var(--color-border-dark);

    summary {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        cursor: pointer;
        list-style: none;
        padding: 22px 4px;
        color: #ffffff;
        font-family: var(--font-heading);
        font-size: 20px;
        font-weight: 600;
        line-height: 1.35;
        letter-spacing: -0.01em;
    }

    summary::-webkit-details-marker {
        display: none;
    }

    summary::after {
        content: '';
        flex-shrink: 0;
        width: 12px;
        height: 12px;
        border-right: 2px solid var(--brand);
        border-bottom: 2px solid var(--brand);
        transform: rotate(45deg) translateY(-2px);
        transition: transform var(--duration-ui) var(--ease-out);
    }

    &[open] summary::after {
        transform: rotate(225deg) translateY(-2px);
    }

    p {
        margin: 0 0 22px;
        max-width: 46em;
        color: rgba(255, 255, 255, 0.8);
        font-size: 16px;
        line-height: 1.6;
    }

    @media (max-width: 690px) {
        summary {
            font-size: 17px;
            padding: 18px 0;
        }
    }
`

export const UtilityGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-top: 36px;

    @media (max-width: 999px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 690px) {
        grid-template-columns: 1fr;
    }
`

export const UtilityCard = styled.article`
    background-color: var(--surface-2);
    border: 1px solid var(--color-border-dark);
    padding: 24px 22px 26px;

    h3 {
        margin: 0 0 6px;
        font-family: var(--font-heading);
        font-size: 18px;
        font-weight: 600;
        line-height: 1.3;
        text-transform: none;
        color: #ffffff;
    }

    p {
        margin: 0 0 14px;
        color: rgba(255, 255, 255, 0.68);
        font-size: 14px;
        line-height: 1.45;
    }

    a {
        color: var(--brand);
        font-family: var(--font-heading);
        font-size: 18px;
        font-weight: 600;
        text-decoration: none;
        letter-spacing: 0.01em;

        &:hover {
            color: var(--brand-hover);
        }
    }
`
